import {
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useMemo, useState } from "react";
import {
  ConfirmActionModal,
  ErrorState,
  LoadingState,
  PageHeader,
  PermissionDenied,
} from "@how/ui";
import { usePlatformRole } from "../auth/platform-role";
import {
  describePlanError,
  formatPrice,
  modulesByPlan,
  PLAN_KEYS,
  SELLING_MODEL_LABEL,
  THEME_ACCESS_LABEL,
  useAllModules,
  useChurchesByPlan,
  useModulePrices,
  usePlanModules,
  usePlans,
  useSaveModulePrice,
  useSavePlan,
  type ModulePrice,
  type Plan,
  type PlanKey,
  type SellingModel,
  type ThemeAccess,
} from "./plans";

interface PlanDraft {
  readonly name: string;
  readonly description: string;
  readonly price: number | "";
  readonly themeAccess: ThemeAccess;
  readonly support: string;
  readonly modules: ReadonlySet<string>;
}

interface PriceDraft {
  readonly sellingModel: SellingModel;
  readonly price: number | "";
  readonly note: string;
}

const centsToDollars = (cents: number | null): number | "" => (cents === null ? "" : cents / 100);
const dollarsToCents = (dollars: number | ""): number | null =>
  dollars === "" ? null : Math.round(dollars * 100);

function draftFor(plan: Plan, modules: ReadonlySet<string>): PlanDraft {
  return {
    name: plan.name,
    description: plan.description,
    price: centsToDollars(plan.monthly_price_cents),
    themeAccess: plan.theme_access as ThemeAccess,
    support: plan.support,
    modules,
  };
}

function priceDraftFor(price: ModulePrice | undefined): PriceDraft {
  return {
    sellingModel: (price?.selling_model as SellingModel | undefined) ?? "add_on",
    price: centsToDollars(price?.monthly_price_cents ?? null),
    note: price?.note ?? "",
  };
}

function sameSet(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false;
  for (const k of a) if (!b.has(k)) return false;
  return true;
}

function planDirty(draft: PlanDraft, plan: Plan, modules: ReadonlySet<string>): boolean {
  return (
    draft.name !== plan.name ||
    draft.description !== plan.description ||
    dollarsToCents(draft.price) !== plan.monthly_price_cents ||
    draft.themeAccess !== plan.theme_access ||
    draft.support !== plan.support ||
    !sameSet(draft.modules, modules)
  );
}

function priceDirty(draft: PriceDraft, price: ModulePrice | undefined): boolean {
  const base = priceDraftFor(price);
  return (
    draft.sellingModel !== base.sellingModel ||
    dollarsToCents(draft.price) !== dollarsToCents(base.price) ||
    draft.note !== base.note
  );
}

/**
 * Plans (ADR 0055, super admin): the three plans side by side with their modules ticked, theme access, price and
 * support, and below them how each add-on is sold on its own. Editing a plan changes it for every church on it, so the
 * save names that number and asks first. Prices are recorded and shown, not charged.
 */
export function PlansPage() {
  const role = usePlatformRole();
  const plans = usePlans();
  const planModules = usePlanModules();
  const prices = useModulePrices();
  const catalog = useAllModules();
  const churches = useChurchesByPlan();
  const savePlan = useSavePlan();
  const savePrice = useSaveModulePrice();
  const canManage = role.data === "super_admin";

  const included = useMemo(() => modulesByPlan(planModules.data ?? []), [planModules.data]);
  const priceByKey = useMemo(
    () => new Map((prices.data ?? []).map((p) => [p.module_key, p] as const)),
    [prices.data],
  );
  const addOns = useMemo(() => (catalog.data ?? []).filter((m) => !m.is_base), [catalog.data]);

  const [drafts, setDrafts] = useState<Partial<Record<PlanKey, PlanDraft>>>({});
  const [priceDrafts, setPriceDrafts] = useState<Record<string, PriceDraft>>({});
  const [confirming, setConfirming] = useState<PlanKey | null>(null);
  // Drafts are taken from the data once, and again after a save clears them, so a refetch never resets what the admin
  // is typing and a mock that returns fresh arrays cannot loop the effect.
  useEffect(() => {
    if (plans.data === undefined || planModules.data === undefined) return;
    if (Object.keys(drafts).length > 0) return;
    setDrafts(Object.fromEntries(plans.data.map((p) => [p.key, draftFor(p, included[p.key])])));
  }, [plans.data, planModules.data, included, drafts]);
  useEffect(() => {
    if (prices.data === undefined || addOns.length === 0) return;
    if (Object.keys(priceDrafts).length > 0) return;
    setPriceDrafts(
      Object.fromEntries(addOns.map((m) => [m.key, priceDraftFor(priceByKey.get(m.key))])),
    );
  }, [prices.data, addOns, priceByKey, priceDrafts]);

  if (
    role.isPending ||
    plans.isPending ||
    planModules.isPending ||
    prices.isPending ||
    catalog.isPending
  ) {
    return <LoadingState rows={8} />;
  }
  if (role.data !== "super_admin" && role.data !== "support") return <PermissionDenied />;
  if (plans.isError || planModules.isError || prices.isError || catalog.isError) {
    return (
      <ErrorState
        message="Could not load the plans."
        onRetry={() => {
          void plans.refetch();
          void planModules.refetch();
          void prices.refetch();
          void catalog.refetch();
        }}
      />
    );
  }

  const planByKey = new Map(plans.data.map((p) => [p.key, p] as const));
  const update = (plan: PlanKey, patch: Partial<PlanDraft>) => {
    setDrafts((d) => {
      const current = d[plan];
      return current === undefined ? d : { ...d, [plan]: { ...current, ...patch } };
    });
  };
  const toggleModule = (plan: PlanKey, moduleKey: string, on: boolean) => {
    setDrafts((d) => {
      const current = d[plan];
      if (current === undefined) return d;
      const next = new Set(current.modules);
      if (on) next.add(moduleKey);
      else next.delete(moduleKey);
      return { ...d, [plan]: { ...current, modules: next } };
    });
  };

  const commitPlan = (plan: PlanKey) => {
    const draft = drafts[plan];
    if (draft === undefined) return;
    savePlan.mutate(
      {
        plan,
        name: draft.name,
        description: draft.description,
        monthlyPriceCents: dollarsToCents(draft.price),
        themeAccess: draft.themeAccess,
        support: draft.support,
        moduleKeys: [...draft.modules],
      },
      {
        onSuccess: () => {
          setConfirming(null);
          setDrafts({});
          notifications.show({
            color: "teal",
            title: `${draft.name} saved`,
            message: `Every church on ${draft.name} has the new set of modules now.`,
          });
        },
        onError: (error) => {
          setConfirming(null);
          notifications.show({
            color: "red",
            title: `${draft.name} was not saved`,
            message: describePlanError(error),
            autoClose: false,
          });
        },
      },
    );
  };

  const commitPrice = (moduleKey: string, name: string) => {
    const draft = priceDrafts[moduleKey];
    if (draft === undefined) return;
    savePrice.mutate(
      {
        moduleKey,
        sellingModel: draft.sellingModel,
        monthlyPriceCents: draft.sellingModel === "usage" ? null : dollarsToCents(draft.price),
        note: draft.note,
      },
      {
        onSuccess: () => {
          setPriceDrafts({});
          notifications.show({
            color: "teal",
            title: `${name} saved`,
            message: "The selling model and price are recorded.",
          });
        },
        onError: (error) =>
          notifications.show({
            color: "red",
            title: `${name} was not saved`,
            message: describePlanError(error),
            autoClose: false,
          }),
      },
    );
  };

  const confirmingDraft = confirming === null ? undefined : drafts[confirming];
  const confirmingCount = confirming === null ? 0 : (churches.data?.[confirming] ?? 0);

  return (
    <>
      <PageHeader
        title="Plans"
        description="Core, Pro and Elite: what each plan includes, its theme access and its price, and how each add-on is sold on its own. Prices are recorded here and shown to nobody at a church until billing exists."
      />
      <Stack gap="md">
        <Card>
          <Title order={4} mb="xs">
            The three plans
          </Title>
          <Text size="sm" c="dimmed" mb="sm">
            {canManage
              ? "Tick a module to include it. Saving a plan changes it for every church on that plan, at once."
              : "Only a platform super admin can change a plan."}
          </Text>
          <Table.ScrollContainer minWidth={760}>
            <Table verticalSpacing="xs" className="how-table">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Module</Table.Th>
                  {PLAN_KEYS.map((key) => {
                    const plan = planByKey.get(key);
                    return (
                      <Table.Th key={key}>
                        <Group gap="xs">
                          <Text fw={600}>{plan?.name ?? key}</Text>
                          <Badge variant="light" color="gray" size="sm">
                            {`${String(churches.data?.[key] ?? 0)} ${(churches.data?.[key] ?? 0) === 1 ? "church" : "churches"}`}
                          </Badge>
                        </Group>
                      </Table.Th>
                    );
                  })}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      Name and description
                    </Text>
                  </Table.Td>
                  {PLAN_KEYS.map((key) => {
                    const draft = drafts[key];
                    return (
                      <Table.Td key={key}>
                        {draft === undefined ? null : (
                          <Stack gap={4}>
                            <TextInput
                              aria-label={`${key} name`}
                              value={draft.name}
                              disabled={!canManage}
                              onChange={(e) => {
                                update(key, { name: e.currentTarget.value });
                              }}
                            />
                            <Textarea
                              aria-label={`${key} description`}
                              autosize
                              minRows={2}
                              value={draft.description}
                              disabled={!canManage}
                              onChange={(e) => {
                                update(key, { description: e.currentTarget.value });
                              }}
                            />
                          </Stack>
                        )}
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      Monthly price
                    </Text>
                    <Text size="xs" c="dimmed">
                      Recorded, not charged
                    </Text>
                  </Table.Td>
                  {PLAN_KEYS.map((key) => {
                    const draft = drafts[key];
                    return (
                      <Table.Td key={key}>
                        {draft === undefined ? null : (
                          <NumberInput
                            aria-label={`${key} monthly price`}
                            prefix="$"
                            min={0}
                            decimalScale={2}
                            value={draft.price}
                            disabled={!canManage}
                            placeholder="Not set"
                            onChange={(value) => {
                              update(key, { price: typeof value === "number" ? value : "" });
                            }}
                          />
                        )}
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      Themes
                    </Text>
                  </Table.Td>
                  {PLAN_KEYS.map((key) => {
                    const draft = drafts[key];
                    return (
                      <Table.Td key={key}>
                        {draft === undefined ? null : (
                          <Select
                            aria-label={`${key} theme access`}
                            value={draft.themeAccess}
                            disabled={!canManage}
                            allowDeselect={false}
                            data={(["included", "premium"] as const).map((v) => ({
                              value: v,
                              label: THEME_ACCESS_LABEL[v],
                            }))}
                            onChange={(value) => {
                              if (value === "included" || value === "premium")
                                update(key, { themeAccess: value });
                            }}
                          />
                        )}
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      Support
                    </Text>
                  </Table.Td>
                  {PLAN_KEYS.map((key) => {
                    const draft = drafts[key];
                    return (
                      <Table.Td key={key}>
                        {draft === undefined ? null : (
                          <TextInput
                            aria-label={`${key} support`}
                            value={draft.support}
                            disabled={!canManage}
                            onChange={(e) => {
                              update(key, { support: e.currentTarget.value });
                            }}
                          />
                        )}
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
                {addOns.map((module) => {
                  const price = priceByKey.get(module.key);
                  const usage = price?.selling_model === "usage";
                  return (
                    <Table.Tr key={module.key}>
                      <Table.Td>
                        <Text size="sm" fw={600}>
                          {module.display_name}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {usage
                            ? "By usage: available on every plan, never in one"
                            : module.status === "planned"
                              ? "Not offered to churches yet; a tick waits"
                              : SELLING_MODEL_LABEL[
                                  (price?.selling_model as SellingModel | undefined) ?? "add_on"
                                ]}
                        </Text>
                      </Table.Td>
                      {PLAN_KEYS.map((key) => {
                        const draft = drafts[key];
                        return (
                          <Table.Td key={key}>
                            <Checkbox
                              aria-label={`${module.display_name} in ${key}`}
                              checked={draft?.modules.has(module.key) ?? false}
                              disabled={!canManage || usage || draft === undefined}
                              onChange={(e) => {
                                toggleModule(key, module.key, e.currentTarget.checked);
                              }}
                            />
                          </Table.Td>
                        );
                      })}
                    </Table.Tr>
                  );
                })}
                {canManage ? (
                  <Table.Tr>
                    <Table.Td />
                    {PLAN_KEYS.map((key) => {
                      const plan = planByKey.get(key);
                      const draft = drafts[key];
                      const dirty =
                        plan !== undefined &&
                        draft !== undefined &&
                        planDirty(draft, plan, included[key]);
                      return (
                        <Table.Td key={key}>
                          <Button
                            size="xs"
                            disabled={!dirty || savePlan.isPending}
                            loading={savePlan.isPending && confirming === key}
                            onClick={() => {
                              setConfirming(key);
                            }}
                          >
                            {`Save ${plan?.name ?? key}`}
                          </Button>
                        </Table.Td>
                      );
                    })}
                  </Table.Tr>
                ) : null}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>

        <Card>
          <Title order={4} mb="xs">
            Add-ons sold on their own
          </Title>
          <Text size="sm" c="dimmed" mb="sm">
            A church on any plan can add a module one by one. Bundled: only with a plan. Add-on: at
            a monthly price. By usage: signed up for separately and paid by use, never in a plan.
          </Text>
          <Table.ScrollContainer minWidth={760}>
            <Table verticalSpacing="xs" className="how-table">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Module</Table.Th>
                  <Table.Th>Sold as</Table.Th>
                  <Table.Th>Monthly price</Table.Th>
                  <Table.Th>Note</Table.Th>
                  {canManage ? <Table.Th /> : null}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {addOns.map((module) => {
                  const draft = priceDrafts[module.key];
                  const price = priceByKey.get(module.key);
                  if (draft === undefined) return null;
                  const dirty = priceDirty(draft, price);
                  return (
                    <Table.Tr key={module.key}>
                      <Table.Td>
                        <Text size="sm" fw={600}>
                          {module.display_name}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {canManage ? (
                          <Select
                            aria-label={`${module.display_name} sold as`}
                            value={draft.sellingModel}
                            allowDeselect={false}
                            data={(["bundled", "add_on", "usage"] as const).map((v) => ({
                              value: v,
                              label: SELLING_MODEL_LABEL[v],
                            }))}
                            onChange={(value) => {
                              if (value === "bundled" || value === "add_on" || value === "usage") {
                                setPriceDrafts((d) => ({
                                  ...d,
                                  [module.key]: { ...draft, sellingModel: value },
                                }));
                              }
                            }}
                          />
                        ) : (
                          <Text size="sm">{SELLING_MODEL_LABEL[draft.sellingModel]}</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {canManage ? (
                          <NumberInput
                            aria-label={`${module.display_name} monthly price`}
                            prefix="$"
                            min={0}
                            decimalScale={2}
                            value={draft.sellingModel === "usage" ? "" : draft.price}
                            disabled={draft.sellingModel === "usage"}
                            placeholder={draft.sellingModel === "usage" ? "By usage" : "Not set"}
                            onChange={(value) => {
                              setPriceDrafts((d) => ({
                                ...d,
                                [module.key]: {
                                  ...draft,
                                  price: typeof value === "number" ? value : "",
                                },
                              }));
                            }}
                          />
                        ) : (
                          <Text size="sm" className="how-numeric">
                            {draft.sellingModel === "usage"
                              ? "By usage"
                              : formatPrice(price?.monthly_price_cents ?? null)}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {canManage ? (
                          <TextInput
                            aria-label={`${module.display_name} note`}
                            value={draft.note}
                            onChange={(e) => {
                              setPriceDrafts((d) => ({
                                ...d,
                                [module.key]: { ...draft, note: e.currentTarget.value },
                              }));
                            }}
                          />
                        ) : (
                          <Text size="sm" c="dimmed">
                            {draft.note}
                          </Text>
                        )}
                      </Table.Td>
                      {canManage ? (
                        <Table.Td>
                          <Button
                            size="xs"
                            variant="default"
                            disabled={!dirty || savePrice.isPending}
                            onClick={() => {
                              commitPrice(module.key, module.display_name);
                            }}
                          >
                            Save
                          </Button>
                        </Table.Td>
                      ) : null}
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      </Stack>
      <ConfirmActionModal
        opened={confirming !== null}
        onClose={() => {
          setConfirming(null);
        }}
        onConfirm={() => {
          if (confirming !== null) commitPlan(confirming);
        }}
        title={`Save ${confirmingDraft?.name ?? "this plan"}?`}
        message={
          confirmingCount === 0
            ? `No church is on ${confirmingDraft?.name ?? "this plan"} today. The change applies to the next church that joins it.`
            : `This changes what ${String(confirmingCount)} ${confirmingCount === 1 ? "church" : "churches"} on ${confirmingDraft?.name ?? "this plan"} can use, at once. A module removed from the plan is hidden from them and their data is kept.`
        }
        confirmLabel="Save plan"
        loading={savePlan.isPending}
      />
    </>
  );
}
